import { composeSchema } from '@plone/volto/helpers/Extensions';
import CustomCSS from './components/CustomCSS/CustomCSS';
import CustomJS from './components/CustomJS/CustomJS';
import LightsiteSettings from './components/LightsiteSettings/LightsiteSettings';
import GlobalColorVariables from './components/GlobalColorVariables/GlobalColorVariables';
import FooterMappingWidget from './widgets/FooterMappingWidget';

import '@plone/volto/config';

// Lightsite reducer — stores settings fetched from the backend registry
const SET_LIGHTSITE_SETTINGS = 'SET_LIGHTSITE_SETTINGS';

const lightsiteReducer = (state = {}, action = {}) => {
  switch (action.type) {
    case SET_LIGHTSITE_SETTINGS:
      return {
        ...state,
        ...action.payload,
      };
    default:
      return state;
  }
};

const applyConfig = (config) => {
  // Register the lightsite reducer
  config.addonReducers = {
    ...config.addonReducers,
    lightsite: lightsiteReducer,
  };

  config.widgets.id = {
    ...config.widgets.id,
    editable_footer_mapping: FooterMappingWidget,
  };

  // Integrate with VLT 8's theme system: define our site colors as proper
  // themes with --theme-color and --theme-foreground-color. VLT 8 components
  // natively use these variables for text, borders, icons, etc.
  // GlobalColorVariables.jsx injects the actual values at runtime.
  config.blocks.themes = [
    ...(config.blocks.themes || []),
    {
      name: 'lightsite-main-color-1',
      label: 'Main Color 1',
      style: {
        '--theme-color': 'var(--lightsite-main-color-1, #ffffff)',
        '--theme-high-contrast-color': 'var(--lightsite-main-color-1, #ffffff)',
        '--theme-foreground-color':
          'var(--lightsite-main-color-text-1, #000000)',
        '--theme-low-contrast-foreground-color':
          'var(--lightsite-main-color-text-1, #000000)',
      },
    },
    {
      name: 'lightsite-main-color-2',
      label: 'Main Color 2',
      style: {
        '--theme-color': 'var(--lightsite-main-color-2, #ffffff)',
        '--theme-high-contrast-color': 'var(--lightsite-main-color-2, #ffffff)',
        '--theme-foreground-color':
          'var(--lightsite-main-color-text-2, #000000)',
        '--theme-low-contrast-foreground-color':
          'var(--lightsite-main-color-text-2, #000000)',
      },
    },
    {
      name: 'lightsite-main-color-3',
      label: 'Main Color 3',
      style: {
        '--theme-color': 'var(--lightsite-main-color-3, #ffffff)',
        '--theme-high-contrast-color': 'var(--lightsite-main-color-3, #ffffff)',
        '--theme-foreground-color':
          'var(--lightsite-main-color-text-3, #000000)',
        '--theme-low-contrast-foreground-color':
          'var(--lightsite-main-color-text-3, #000000)',
      },
    },
  ];

  // Update highlight block to have our customized colors
  config.blocks.blocksConfig.highlight = {
    ...config.blocks.blocksConfig.highlight,
    descriptionColors: [
      { name: 'lightsite-main-color-1', label: 'Main Color 1' },
      { name: 'lightsite-main-color-2', label: 'Main Color 2' },
      { name: 'lightsite-main-color-3', label: 'Main Color 3' },
    ],
  };

  // Whitelist blocks allowed for the footer component.
  const footerAllowedBlocks = ['slate', 'image', 'gridBlock', 'html'];
  Object.keys(config.blocks.blocksConfig).forEach((blockId) => {
    const blockConfig = config.blocks.blocksConfig[blockId];
    const originalRestricted = blockConfig.restricted;

    blockConfig.restricted = ({ contentType }) => {
      if (contentType === 'Footer') {
        return !footerAllowedBlocks.includes(blockId);
      }
      return originalRestricted;
    };
  });
  config.blocks.blocksConfig.gridBlock.allowedBlocks = [
    'image',
    'listing',
    'slate',
    'teaser',
  ];

  // Re-expose the accordion `collapsed` checkbox that volto-light-theme strips
  // from the sidebar. When checked, the upstream View keeps all panels closed.
  const accordion = config.blocks.blocksConfig.accordion;
  accordion.schemaEnhancer = composeSchema(
    accordion.schemaEnhancer,
    ({ schema }) => {
      if (
        schema.properties.collapsed &&
        !schema.fieldsets.find((fieldset) => fieldset.id === 'options')
      ) {
        schema.fieldsets.push({
          id: 'options',
          title: 'Options',
          fields: ['collapsed'],
        });
      }
      return schema;
    },
  );

  const appExtras = [
    {
      match: '',
      component: LightsiteSettings,
    },
    {
      match: '',
      component: CustomCSS,
    },
    {
      match: '',
      component: CustomJS,
    },
    {
      match: '',
      component: GlobalColorVariables,
    },
  ];
  config.settings.appExtras = [...config.settings.appExtras, ...appExtras];

  return config;
};

export default applyConfig;
